// src/features/TeamChat/api/teamChat.api.ts
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import { dropEchoedPending, settlePending } from './teamChat.pending'
import { applyRequestToResponse, mergeMessages, mergeNewestPage } from './teamChat.transform'
import type {
  SendMessageArgs,
  SendMessageResponse,
  TeamMembersResponse,
  TeamMessage,
  TeamMessagesPage,
  TeamRequest,
  TeamRequestAction,
  TeamRequestChange,
  TeamRequestFilter,
  TeamRequestsResponse,
  TeamSummary,
} from './teamChat.types'

export const TEAM_MEMBERS_TAG = 'TeamMembers'
export const TEAM_SUMMARY_TAG = 'TeamSummary'
export const TEAM_MESSAGES_TAG = 'TeamMessages'
export const TEAM_REQUESTS_TAG = 'TeamRequests'
export const TEAM_PAGE_SIZE = 50
const FILTERS: TeamRequestFilter[] = ['active', 'done']

// A POST retried by the base query would post twice.
const NO_RETRY = { maxRetries: 0 }

// RTK thunks carry their own state type, so state and extra stay open here.
export type TeamDispatch = ThunkDispatch<any, any, UnknownAction>

type ChangeApi = { dispatch: TeamDispatch; queryFulfilled: Promise<{ data: TeamRequestChange }> }

export const teamChatApi = createApi({
  reducerPath: REDUCERS.TEAM_CHAT,
  baseQuery: baseQuery(),
  tagTypes: [TEAM_MEMBERS_TAG, TEAM_SUMMARY_TAG, TEAM_MESSAGES_TAG, TEAM_REQUESTS_TAG],
  endpoints: (build) => ({
    getTeamMembers: build.query<TeamMembersResponse, void>({
      query: () => ({ url: '/team/members' }),
      providesTags: [TEAM_MEMBERS_TAG],
    }),

    getTeamSummary: build.query<TeamSummary, void>({
      query: () => ({ url: '/team/summary' }),
      providesTags: [TEAM_SUMMARY_TAG],
    }),

    getTeamMessages: build.query<TeamMessagesPage, void>({
      query: () => ({ url: '/team/messages', params: { limit: TEAM_PAGE_SIZE } }),
      providesTags: [TEAM_MESSAGES_TAG],
      merge: (cache, incoming) => mergeNewestPage(cache, incoming),
    }),

    getOlderTeamMessages: build.query<TeamMessagesPage, { before: number }>({
      query: ({ before }) => ({ url: '/team/messages', params: { before, limit: TEAM_PAGE_SIZE } }),
      keepUnusedDataFor: 0,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled.catch(() => ({ data: null }))
        if (data) dispatch(patchOlderPage(data))
      },
    }),

    getTeamRequests: build.query<TeamRequestsResponse, TeamRequestFilter>({
      query: (filter) => ({ url: '/team/requests', params: { filter } }),
      providesTags: [TEAM_REQUESTS_TAG],
    }),

    sendTeamMessage: build.mutation<SendMessageResponse, SendMessageArgs>({
      query: ({ body, create_request }) => ({
        url: '/team/messages',
        method: HTTP_METHODS.POST,
        data: { body, create_request },
      }),
      extraOptions: NO_RETRY,
      invalidatesTags: [TEAM_SUMMARY_TAG],
      // The bubble shows at once; the server copy replaces it, or it goes away on failure.
      async onQueryStarted({ pending }, { dispatch, queryFulfilled }) {
        dispatch(patchMessages([pending]))
        const { data } = await queryFulfilled.catch(() => ({ data: null }))
        const confirmed = data
          ? [data.message, data.event_message].filter((m): m is TeamMessage => Boolean(m))
          : []
        dispatch(replacePending(pending.id, confirmed))
        if (data?.request) dispatch(patchRequest(data.request))
      },
    }),

    markTeamRead: build.mutation<
      { last_read_message_id: number },
      { last_read_message_id?: number }
    >({
      query: (data) => ({ url: '/team/messages/read', method: HTTP_METHODS.POST, data }),
      extraOptions: NO_RETRY,
      invalidatesTags: [TEAM_SUMMARY_TAG],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled.catch(() => ({ data: null }))
        if (data) dispatch(patchLastRead(data.last_read_message_id))
      },
    }),

    createTeamRequest: build.mutation<TeamRequestChange, { title: string }>({
      query: (data) => ({ url: '/team/requests', method: HTTP_METHODS.POST, data }),
      extraOptions: NO_RETRY,
      invalidatesTags: [TEAM_SUMMARY_TAG],
      onQueryStarted: (_arg, api) => applyChangeWhenDone(api),
    }),

    updateTeamRequest: build.mutation<TeamRequestChange, { id: number; action: TeamRequestAction }>(
      {
        query: ({ id, action }) => ({
          url: `/team/requests/${id}/${action}`,
          method: HTTP_METHODS.POST,
        }),
        extraOptions: NO_RETRY,
        invalidatesTags: [TEAM_SUMMARY_TAG],
        onQueryStarted: (_arg, api) => applyChangeWhenDone(api),
      },
    ),
  }),
})

async function applyChangeWhenDone({ dispatch, queryFulfilled }: ChangeApi): Promise<void> {
  const { data } = await queryFulfilled.catch(() => ({ data: null }))
  if (!data) return
  dispatch(patchRequest(data.request))
  dispatch(patchMessages([data.event_message]))
}

export function patchMessages(messages: TeamMessage[]) {
  return teamChatApi.util.updateQueryData('getTeamMessages', undefined, (draft) => ({
    ...draft,
    messages: mergeMessages(dropEchoedPending(draft.messages, messages), messages),
  }))
}

function replacePending(pendingId: number, confirmed: TeamMessage[]) {
  return teamChatApi.util.updateQueryData('getTeamMessages', undefined, (draft) => ({
    ...draft,
    messages: settlePending(draft.messages, pendingId, confirmed),
  }))
}

function patchOlderPage(older: TeamMessagesPage) {
  return teamChatApi.util.updateQueryData('getTeamMessages', undefined, (draft) => ({
    ...draft,
    messages: mergeMessages(draft.messages, older.messages),
    has_more: older.has_more,
  }))
}

function patchLastRead(lastReadId: number) {
  return teamChatApi.util.updateQueryData('getTeamMessages', undefined, (draft) => ({
    ...draft,
    last_read_message_id: lastReadId,
  }))
}

export function patchRequest(request: TeamRequest) {
  return (dispatch: TeamDispatch) =>
    FILTERS.forEach((filter) =>
      dispatch(
        teamChatApi.util.updateQueryData('getTeamRequests', filter, (draft) =>
          applyRequestToResponse(draft, request, filter),
        ),
      ),
    )
}

export const {
  useGetTeamMembersQuery,
  useGetTeamSummaryQuery,
  useGetTeamMessagesQuery,
  useLazyGetOlderTeamMessagesQuery,
  useGetTeamRequestsQuery,
  useSendTeamMessageMutation,
  useMarkTeamReadMutation,
  useCreateTeamRequestMutation,
  useUpdateTeamRequestMutation,
} = teamChatApi
